import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface TicketTier {
  id: string;
  name: string;
  price: string | number;
  description: string;
  active: boolean;
}

interface TierSelectorProps {
  registrationId: string;
  onPurchaseSuccess?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '1.5rem',
  },
  title: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    padding: '1.25rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    backgroundColor: '#fff',
  },
  cardSelected: {
    padding: '1.25rem',
    border: '2px solid #1a6b3c',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: '#f0f9f4',
    boxShadow: '0 2px 8px rgba(26, 107, 60, 0.15)',
  },
  cardName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f2d1f',
    marginBottom: '0.5rem',
  },
  cardPrice: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a6b3c',
    marginBottom: '0.5rem',
  },
  cardDescription: {
    fontSize: '0.85rem',
    color: '#666',
    lineHeight: 1.4,
  },
  purchaseBtn: {
    padding: '0.6rem 1.5rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  purchaseBtnDisabled: {
    padding: '0.6rem 1.5rem',
    backgroundColor: '#a0a0a0',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  loading: {
    color: '#666',
    fontSize: '0.9rem',
    padding: '1rem 0',
  },
  emptyState: {
    color: '#666',
    fontSize: '0.9rem',
    padding: '1rem 0',
  },
};

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function TierSelector({ registrationId, onPurchaseSuccess }: TierSelectorProps) {
  const [tiers, setTiers] = useState<TicketTier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchTiers() {
      try {
        const data = await apiClient<TicketTier[]>('/legacy-ball/tiers');
        const sorted = [...data].sort((a, b) => {
          const priceA = typeof a.price === 'string' ? parseFloat(a.price) : a.price;
          const priceB = typeof b.price === 'string' ? parseFloat(b.price) : b.price;
          return priceA - priceB;
        });
        setTiers(sorted);
      } catch {
        setError('Failed to load ticket tiers.');
      } finally {
        setLoading(false);
      }
    }
    fetchTiers();
  }, []);

  async function handlePurchase() {
    if (!selectedTierId || !registrationId) return;
    setError('');
    setPurchasing(true);
    try {
      await apiClient('/legacy-ball/tickets', {
        method: 'POST',
        body: { registrationId, tierId: selectedTierId },
      });
      onPurchaseSuccess?.();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to purchase ticket.');
    } finally {
      setPurchasing(false);
    }
  }

  if (loading) {
    return <div style={styles.loading}>Loading ticket tiers...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Select a Ticket Tier</h2>
      {error && <div style={styles.error}>{error}</div>}
      {tiers.length === 0 ? (
        <p style={styles.emptyState}>No ticket tiers are currently available.</p>
      ) : (
        <>
          <div style={styles.cardsGrid}>
            {tiers.map((tier) => (
              <div
                key={tier.id}
                style={selectedTierId === tier.id ? styles.cardSelected : styles.card}
                onClick={() => setSelectedTierId(tier.id)}
                role="button"
                tabIndex={0}
                aria-pressed={selectedTierId === tier.id}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedTierId(tier.id);
                  }
                }}
              >
                <div style={styles.cardName}>{tier.name}</div>
                <div style={styles.cardPrice}>{formatCurrency(tier.price)}</div>
                {tier.description && (
                  <div style={styles.cardDescription}>{tier.description}</div>
                )}
              </div>
            ))}
          </div>
          <button
            style={selectedTierId ? styles.purchaseBtn : styles.purchaseBtnDisabled}
            disabled={!selectedTierId || purchasing}
            onClick={handlePurchase}
          >
            {purchasing ? 'Purchasing...' : 'Confirm Purchase'}
          </button>
        </>
      )}
    </div>
  );
}
